/**
 * KAN-951: selected help section and the "watch everything" playlist.
 *
 * The section comes from the URL (/help/:slug); /help opens the first
 * visible section, an unknown or hidden slug is replaced in the URL by the
 * first visible one (once the role is known, so an owner's link is not
 * rewritten while the user is loading). In playlist mode the end of a video,
 * or a video that cannot be played, moves to the next section of the role's
 * list and starts it; after the last one the mode switches off.
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { findHelpItem, type HelpItem } from '@/tutorials/content';

export interface HelpPlayer {
  current: HelpItem | null;
  playAll: boolean;
  togglePlayAll: () => void;
  onVideoEnded: () => void;
  onVideoFailed: () => void;
  videoRef: RefObject<HTMLVideoElement>;
}

export function helpPath(item: HelpItem): string {
  return `/help/${item.slug}`;
}

function startVideo(video: HTMLVideoElement | null): void {
  const started = video?.play();
  if (started && typeof started.catch === 'function') {
    // Browser autoplay policy may refuse; the player keeps its controls.
    started.catch(() => {});
  }
}

export interface HelpPlayerOptions {
  /** Role is known (current user loaded): the URL may be corrected. */
  ready: boolean;
}

export function useHelpPlayer(items: HelpItem[], { ready }: HelpPlayerOptions): HelpPlayer {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playAll, setPlayAll] = useState(false);
  const pendingStart = useRef(false);

  const current = findHelpItem(slug, items) ?? items[0] ?? null;
  const currentSlug = current?.slug;

  // Unknown or hidden slug: the address follows the section on screen.
  useEffect(() => {
    if (ready && slug && currentSlug && slug !== currentSlug) {
      navigate(`/help/${currentSlug}`, { replace: true });
    }
  }, [ready, slug, currentSlug, navigate]);

  // The next section's video mounts after navigation; start it then.
  useEffect(() => {
    if (!pendingStart.current) return;
    pendingStart.current = false;
    startVideo(videoRef.current);
  }, [currentSlug]);

  const togglePlayAll = useCallback(() => {
    if (!playAll) startVideo(videoRef.current);
    setPlayAll(!playAll);
  }, [playAll]);

  const playNext = useCallback(() => {
    if (!playAll || !current) return;
    const index = items.findIndex((item) => item.slug === current.slug);
    const next = index >= 0 ? items[index + 1] : undefined;
    if (!next) {
      setPlayAll(false);
      return;
    }
    pendingStart.current = true;
    navigate(helpPath(next), { replace: true });
  }, [playAll, current, items, navigate]);

  return {
    current,
    playAll,
    togglePlayAll,
    onVideoEnded: playNext,
    onVideoFailed: playNext,
    videoRef,
  };
}
